/**
 * Sanitises an external redirect target to a same-origin path
 * (`pathname + search + hash`); guards against open-redirect attacks.
 * Use with React Router (`navigate`, `history.push`). For
 * `window.location.*` use `safeRedirectURL`.
 */
export const safeRedirectPath = (input: string): string => {
  if (!input.trim()) return '';
  try {
    const parsed = new URL(input, window.location.origin);
    // Defend against non-http(s) protocols, eg. `javascript:alert(1)`
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return '/';
    }
    // Collapse leading multi-slashes — `new URL('https://x.com//a/b').pathname`
    // is `//a/b`, which React Router/pushState would treat as cross-origin.
    const pathname = parsed.pathname.replace(/^\/+/, '/');
    return `${pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return '';
  }
};

/**
 * Sanitises an external redirect target to a same-origin URL — composes
 * `window.location.origin` with `safeRedirectPath`. Use for
 * `window.location.*` and SAML RelayState. For React Router use
 * `safeRedirectPath`.
 */
export const safeRedirectURL = (input: string): string =>
  `${window.location.origin}${safeRedirectPath(input)}`;
